import { picoSearch } from '@chatwoot/pico-search';

export const INBOX_TYPES = {
  WEB: 'Channel::WebWidget',
  FB: 'Channel::FacebookPage',
  TWITTER: 'Channel::TwitterProfile',
  TWILIO: 'Channel::TwilioSms',
  WHATSAPP: 'Channel::Whatsapp',
  API: 'Channel::Api',
  EMAIL: 'Channel::Email',
  TELEGRAM: 'Channel::Telegram',
  LINE: 'Channel::Line',
  SMS: 'Channel::Sms',
  INSTAGRAM: 'Channel::Instagram',
  TIKTOK: 'Channel::Tiktok',
};

// Short channel-type slugs used to identify a channel without leaning on its
// Channel:: class name — e.g. onboarding channel cards and OAuth provider maps.
export const CHANNEL_TYPES = {
  WEBSITE: 'website',
  WHATSAPP: 'whatsapp',
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok',
  TELEGRAM: 'telegram',
  LINE: 'line',
  GMAIL: 'gmail',
  OUTLOOK: 'outlook',
  SMS: 'sms',
  API: 'api',
  VOICE: 'voice',
  EMAIL: 'email',
};

// Add providers here as they gain voice capability (e.g., WhatsApp Cloud, Twilio WhatsApp)
export const VOICE_CALL_PROVIDERS = {
  TWILIO: 'twilio',
  WHATSAPP: 'whatsapp',
};

export const getVoiceCallProvider = inbox => {
  if (!inbox) return null;

  // Callers pass either snake_case (raw API) or camelCase (after camelcaseKeys) shapes.
  const channelType = inbox.channel_type || inbox.channelType;
  const voiceEnabled = inbox.voice_enabled || inbox.voiceEnabled;

  if (!voiceEnabled) return null;

  if (channelType === INBOX_TYPES.TWILIO) return VOICE_CALL_PROVIDERS.TWILIO;
  if (channelType === INBOX_TYPES.WHATSAPP)
    return VOICE_CALL_PROVIDERS.WHATSAPP;
  if (
    channelType === INBOX_TYPES.API &&
    (inbox.evolution_unofficial ||
      inbox.evolutionUnofficial ||
      inbox.additional_attributes?.evolution_provider ===
        'whatsapp_unofficial') &&
    voiceEnabled
  ) {
    return VOICE_CALL_PROVIDERS.WHATSAPP;
  }

  return null;
};

export const getVoiceBridgeMode = inbox => {
  if (!inbox) return null;
  const explicit = inbox.voice_bridge_mode || inbox.voiceBridgeMode;
  if (explicit) return explicit;
  // Evolution unofficial inboxes always use the Baileys media bridge; keep a
  // fallback when a partial inbox payload omits voice_bridge_mode after merges.
  if (
    inbox.evolution_unofficial ||
    inbox.evolutionUnofficial ||
    inbox.additional_attributes?.evolution_provider === 'whatsapp_unofficial'
  ) {
    return 'baileys';
  }
  return null;
};

export const isVoiceCallEnabled = inbox => getVoiceCallProvider(inbox) !== null;

export const isAccountWhatsappCallingEnabled = account =>
  account?.settings?.whatsapp_calling_enabled !== false;

export const isWhatsappMessagingInbox = inbox => {
  if (!inbox) return false;

  const channelType = inbox.channel_type || inbox.channelType;
  if (channelType === INBOX_TYPES.WHATSAPP) return true;
  if (
    channelType === INBOX_TYPES.API &&
    (inbox.evolution_unofficial ||
      inbox.evolutionUnofficial ||
      inbox.additional_attributes?.evolution_provider === 'whatsapp_unofficial')
  ) {
    return true;
  }
  if (channelType === INBOX_TYPES.TWILIO) {
    const medium = inbox.medium;
    const phone = String(inbox.phone_number || inbox.phoneNumber || '');
    return medium === 'whatsapp' || phone.startsWith('whatsapp');
  }
  return false;
};

export const inboxPhoneNumber = inbox => {
  if (!inbox) return '';

  const attrs = inbox.additional_attributes || inbox.additionalAttributes || {};
  return (
    inbox.phone_number ||
    inbox.phoneNumber ||
    attrs.phone_number ||
    attrs.phoneNumber ||
    ''
  ).toString();
};

export const findAssigneeWhatsappInbox = (inboxes, userId) => {
  const id = Number(userId);
  if (!id) return null;

  return (
    (inboxes || []).find(inbox => {
      if (!isWhatsappMessagingInbox(inbox)) return false;
      const ownerId = Number(inbox.owner_user_id || inbox.ownerUserId);
      return ownerId === id;
    }) || null
  );
};

export const isEvolutionUnofficialInbox = inbox =>
  Boolean(
    inbox?.evolution_unofficial ||
      inbox?.evolutionUnofficial ||
      inbox?.additional_attributes?.evolution_provider === 'whatsapp_unofficial'
  );

// Personal Evolution lines (owner_user_id set) are owner-write-only.
// Company Cloud / unowned inboxes stay writable for any member.
export const canAgentWriteToInbox = (inbox, userId) => {
  if (!inbox) return false;
  if (!isEvolutionUnofficialInbox(inbox)) return true;

  const ownerId = Number(inbox.owner_user_id || inbox.ownerUserId);
  if (!ownerId) return true;
  return ownerId === Number(userId);
};

export const writableInboxesForAgent = (inboxes, userId) =>
  (inboxes || []).filter(inbox => canAgentWriteToInbox(inbox, userId));

const inboxOwnerUserId = inbox =>
  Number(inbox?.owner_user_id || inbox?.ownerUserId) || 0;

const sortCompanyWhatsappFirst = (left, right) => {
  const ownedDiff =
    Number(Boolean(inboxOwnerUserId(left))) -
    Number(Boolean(inboxOwnerUserId(right)));
  if (ownedDiff !== 0) return ownedDiff;
  return Number(left.id) - Number(right.id);
};

export const writableWhatsappInboxesForTicket = (
  inboxes,
  { currentUserId } = {}
) =>
  (inboxes || [])
    .filter(
      inbox =>
        isWhatsappMessagingInbox(inbox) &&
        canAgentWriteToInbox(inbox, currentUserId)
    )
    .slice()
    .sort(sortCompanyWhatsappFirst);

export const writableVoiceInboxesForTicket = (inboxes, options) =>
  writableWhatsappInboxesForTicket(inboxes, options).filter(isVoiceCallEnabled);

export const pickTicketWhatsappInbox = (
  inboxes,
  { currentUserId, assigneeId } = {}
) => {
  const writable = writableWhatsappInboxesForTicket(inboxes, { currentUserId });
  if (!writable.length) return null;

  return (
    findAssigneeWhatsappInbox(writable, assigneeId) ||
    findAssigneeWhatsappInbox(writable, currentUserId) ||
    writable.find(
      inbox => isEvolutionUnofficialInbox(inbox) && inboxPhoneNumber(inbox)
    ) ||
    writable.find(inbox => inboxPhoneNumber(inbox)) ||
    writable[0]
  );
};

export const pickTicketVoiceInbox = (
  inboxes,
  { currentUserId, preferredInbox } = {}
) => {
  const writable = writableVoiceInboxesForTicket(inboxes, { currentUserId });
  if (!writable.length) return null;

  const preferredId = Number(preferredInbox?.id);
  return (
    writable.find(inbox => Number(inbox.id) === preferredId) || writable[0]
  );
};

export const evolutionInstanceName = inbox =>
  inbox?.evolution_instance_name ||
  inbox?.evolutionInstanceName ||
  inbox?.additional_attributes?.evolution_instance_name ||
  '';

const AFTER_SALES_STATUS_RANK = {
  open: 0,
  pending: 1,
  snoozed: 2,
};

export const pickAfterSalesConversation = (conversations, inboxId) => {
  const matches = (conversations || []).filter(
    conv => Number(conv.inbox_id) === Number(inboxId)
  );
  if (!matches.length) return null;

  return [...matches].sort((left, right) => {
    const rankDiff =
      (AFTER_SALES_STATUS_RANK[left.status] ?? 3) -
      (AFTER_SALES_STATUS_RANK[right.status] ?? 3);
    if (rankDiff !== 0) return rankDiff;
    return (right.last_activity_at || 0) - (left.last_activity_at || 0);
  })[0];
};

// Combined channel + voice-wave badge glyph per voice-call provider.
export const VOICE_CALL_ICONS = {
  [VOICE_CALL_PROVIDERS.WHATSAPP]: 'i-woot-whatsapp-voice',
  [VOICE_CALL_PROVIDERS.TWILIO]: 'i-woot-voice-call',
};

export const getVoiceCallIcon = provider =>
  VOICE_CALL_ICONS[provider] ?? VOICE_CALL_ICONS[VOICE_CALL_PROVIDERS.TWILIO];

export const TWILIO_CHANNEL_MEDIUM = {
  WHATSAPP: 'whatsapp',
  SMS: 'sms',
};

export const getInboxVoiceIcon = (channelType, medium) => {
  const isWhatsapp =
    channelType === INBOX_TYPES.WHATSAPP ||
    (channelType === INBOX_TYPES.TWILIO &&
      medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP);
  return getVoiceCallIcon(
    isWhatsapp ? VOICE_CALL_PROVIDERS.WHATSAPP : VOICE_CALL_PROVIDERS.TWILIO
  );
};

const INBOX_ICON_MAP_FILL = {
  [INBOX_TYPES.WEB]: 'i-ri-global-fill',
  [INBOX_TYPES.FB]: 'i-ri-messenger-fill',
  [INBOX_TYPES.TWITTER]: 'i-ri-twitter-x-fill',
  [INBOX_TYPES.WHATSAPP]: 'i-ri-whatsapp-fill',
  [INBOX_TYPES.API]: 'i-ri-cloudy-fill',
  [INBOX_TYPES.EMAIL]: 'i-ri-mail-fill',
  [INBOX_TYPES.TELEGRAM]: 'i-ri-telegram-fill',
  [INBOX_TYPES.LINE]: 'i-ri-line-fill',
  [INBOX_TYPES.INSTAGRAM]: 'i-ri-instagram-fill',
  [INBOX_TYPES.TIKTOK]: 'i-ri-tiktok-fill',
};

const DEFAULT_ICON_FILL = 'i-ri-chat-1-fill';

const INBOX_ICON_MAP_LINE = {
  [INBOX_TYPES.WEB]: 'i-woot-website',
  [INBOX_TYPES.FB]: 'i-woot-messenger',
  [INBOX_TYPES.TWITTER]: 'i-woot-x',
  [INBOX_TYPES.WHATSAPP]: 'i-woot-whatsapp',
  [INBOX_TYPES.API]: 'i-woot-api',
  [INBOX_TYPES.EMAIL]: 'i-woot-mail',
  [INBOX_TYPES.TELEGRAM]: 'i-woot-telegram',
  [INBOX_TYPES.LINE]: 'i-woot-line',
  [INBOX_TYPES.INSTAGRAM]: 'i-woot-instagram',
  [INBOX_TYPES.TIKTOK]: 'i-woot-tiktok',
};

const DEFAULT_ICON_LINE = 'i-ri-chat-1-line';

// Facebook, Instagram, TikTok, and X initialize the editable inbox name from
// the provider account name; their opaque routing IDs should not be displayed.
const INBOX_IDENTIFIER_RESOLVERS = {
  [INBOX_TYPES.WEB]: inbox => inbox.website_url,
  [INBOX_TYPES.EMAIL]: inbox => inbox.email,
  [INBOX_TYPES.WHATSAPP]: inbox => inbox.phone_number,
  [INBOX_TYPES.SMS]: inbox => inbox.phone_number,
  [INBOX_TYPES.LINE]: inbox => inbox.line_channel_id,
  [INBOX_TYPES.API]: inbox => inbox.inbox_identifier,
  [INBOX_TYPES.TWILIO]: inbox =>
    inbox.phone_number?.replace(/^whatsapp:/, '') ||
    inbox.messaging_service_sid ||
    '',
  [INBOX_TYPES.TELEGRAM]: inbox => {
    if (!inbox.bot_name) return '';
    return inbox.bot_name.startsWith('@')
      ? inbox.bot_name
      : `@${inbox.bot_name}`;
  },
};

export const getInboxIdentifier = inbox =>
  INBOX_IDENTIFIER_RESOLVERS[inbox?.channel_type]?.(inbox) || '';

export const searchInboxes = (inboxes, query) => {
  const primaryMatches = picoSearch(inboxes, query, ['name', 'channel_type']);
  const primaryMatchIds = new Set(primaryMatches.map(inbox => inbox.id));
  const identifierMatches = picoSearch(inboxes, query, ['channel_identifier']);

  return [
    ...primaryMatches,
    ...identifierMatches.filter(inbox => !primaryMatchIds.has(inbox.id)),
  ];
};

export const getReadableInboxByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'livechat';

    case INBOX_TYPES.FB:
      return 'facebook';

    case INBOX_TYPES.TWITTER:
      return 'twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp') ? 'whatsapp' : 'sms';

    case INBOX_TYPES.WHATSAPP:
      return 'whatsapp';

    case INBOX_TYPES.API:
      return 'api';

    case INBOX_TYPES.EMAIL:
      return 'email';

    case INBOX_TYPES.TELEGRAM:
      return 'telegram';

    case INBOX_TYPES.LINE:
      return 'line';

    default:
      return 'chat';
  }
};

export const getInboxClassByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'globe-desktop';

    case INBOX_TYPES.FB:
      return 'brand-facebook';

    case INBOX_TYPES.TWITTER:
      return 'brand-twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp')
        ? 'brand-whatsapp'
        : 'brand-sms';

    case INBOX_TYPES.WHATSAPP:
      return 'brand-whatsapp';

    case INBOX_TYPES.API:
      return 'cloud';

    case INBOX_TYPES.EMAIL:
      return 'mail';

    case INBOX_TYPES.TELEGRAM:
      return 'brand-telegram';

    case INBOX_TYPES.LINE:
      return 'brand-line';

    case INBOX_TYPES.INSTAGRAM:
      return 'brand-instagram';

    case INBOX_TYPES.TIKTOK:
      return 'brand-tiktok';

    default:
      return 'chat';
  }
};

export const getInboxIconByType = (
  type,
  medium,
  variant = 'fill',
  voiceEnabled = false
) => {
  if (voiceEnabled) return getInboxVoiceIcon(type, medium);

  const iconMap =
    variant === 'fill' ? INBOX_ICON_MAP_FILL : INBOX_ICON_MAP_LINE;
  const defaultIcon =
    variant === 'fill' ? DEFAULT_ICON_FILL : DEFAULT_ICON_LINE;

  // Special case for Twilio (whatsapp and sms)
  if (type === INBOX_TYPES.TWILIO && medium === 'whatsapp') {
    return iconMap[INBOX_TYPES.WHATSAPP];
  }

  return iconMap[type] ?? defaultIcon;
};

export const getInboxWarningIconClass = (type, reauthorizationRequired) => {
  const allowedInboxTypes = [INBOX_TYPES.FB, INBOX_TYPES.EMAIL];
  if (allowedInboxTypes.includes(type) && reauthorizationRequired) {
    return 'warning';
  }
  return '';
};
